import classNames from 'classnames'
import React, { ReactNode, CSSProperties, useRef } from 'react'
import { useMemoizedFn } from 'ahooks'
import CustomIcon from '@/components/CustomIcon'
import styles from './index.less'

const MAX_COUNT_VALUE = 9999

export interface QuickSearchOption<T extends string = string> {
    label: ReactNode
    briefLabel?: ReactNode
    value: T
    count?: number
    countStyles?: object
}

export interface QuickSearchGroupItem<T extends string = string> {
    key: string
    hidden?: boolean
    label: ReactNode
    options: QuickSearchOption<T>[]
}

export interface QuickSearchGroupProps<T extends string = string> {
    value?: T
    groups?: QuickSearchGroupItem<T>[]
    onQuickSearchChange: (type: T, checked: boolean) => void
    style?: CSSProperties
}

export const QuickSearchGroups = <T extends string = string>(props: QuickSearchGroupProps<T>) => {
    const { groups = [], onQuickSearchChange, value, style } = props

    const div$ = useRef<HTMLDivElement>(null)

    const renderOptionItem = useMemoizedFn((optionItem: QuickSearchOption<T>) => {
        const { label, value: optionValue, count = 0, countStyles } = optionItem

        const isChecked = value === optionValue

        const className = classNames(styles.quickSearchOptionItem, { [styles.checked]: isChecked })

        const handleClick = () => {
            div$.current?.scrollIntoView({ behavior: 'smooth' })
            onQuickSearchChange(optionValue, !isChecked)
        }

        return (
            <div key={optionValue} className={className} onClick={handleClick}>
                <div className={styles.checkedIcon}>
                    <CustomIcon type="icon-check-line" />
                </div>
                <div className={styles.quickSearchOptionItemLabel}>{label}</div>
                <div className={styles.quickSearchOptionItemCount} style={countStyles || {}}>
                    {count > MAX_COUNT_VALUE ? '9999+' : count}
                </div>
            </div>
        )
    })

    return (
        <div ref={div$} className={styles.groupList} style={style}>
            {groups.map(groupItem => (
                <div key={groupItem.key} className={styles.groupItem}>
                    <div className={styles.groupItemTitle}>{groupItem.label}</div>
                    <div className={styles.groupItemOptionList}>{groupItem.options.map(renderOptionItem)}</div>
                </div>
            ))}
        </div>
    )
}
